import React from "react";
import InputMask from "react-input-mask";
import { UserPlus, X } from "lucide-react";
import { NewPatientForm } from "../../types/supabase";
import {
  calculateIMC,
  formatPatientPhoneInput,
  getIMCClassification,
} from "../../utils/patientRegistration";

interface PatientRegistrationModalProps {
  formData: NewPatientForm;
  onCancel: () => void;
  onCepBlur: (cep: string) => void | Promise<void>;
  onClose: () => void;
  onConsentChange: (checked: boolean) => void;
  onFormChange: (field: keyof NewPatientForm, value: string | number) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  consentGiven: boolean;
  isSubmitting: boolean;
}

const PatientRegistrationModal: React.FC<PatientRegistrationModalProps> = ({
  formData,
  onCancel,
  onCepBlur,
  onClose,
  onConsentChange,
  onFormChange,
  onSubmit,
  consentGiven,
  isSubmitting,
}) => {
  const imc = calculateIMC(formData.peso, formData.altura_x0020__x0028_cm_x0029_);
  const shouldShowImc = formData.altura_x0020__x0028_cm_x0029_ > 0 && formData.peso > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <UserPlus className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Cadastrar Novo Paciente</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Dados Pessoais</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(event) => onFormChange("nome", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sobrenome *</label>
                  <input
                    type="text"
                    value={formData.sobrenome}
                    onChange={(event) => onFormChange("sobrenome", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                  <InputMask
                    mask="999.999.999-99"
                    value={formData.patientCpf}
                    onChange={(event) => onFormChange("patientCpf", event.target.value)}
                  >
                    {(inputProps: any) => (
                      <input
                        {...inputProps}
                        type="text"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    )}
                  </InputMask>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">RG</label>
                  <input
                    type="text"
                    value={formData.patientRg}
                    onChange={(event) => onFormChange("patientRg", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Informe o RG"
                    maxLength={20}
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-gray-500">Preencha pelo menos um documento: CPF ou RG.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
                  <input
                    type="date"
                    value={formData.data_x0020_de_x0020_nascimento}
                    onChange={(event) => onFormChange("data_x0020_de_x0020_nascimento", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sexo *</label>
                  <select
                    value={formData.sexo}
                    onChange={(event) => onFormChange("sexo", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.patientEmail}
                    onChange={(event) => onFormChange("patientEmail", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="paciente@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Celular/WhatsApp</label>
                  <input
                    type="tel"
                    value={formData.patientPhone}
                    onChange={(event) => onFormChange("patientPhone", formatPatientPhoneInput(event.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="(43) 99999-9999"
                    inputMode="tel"
                  />
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-4">Dados Físicos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Altura (cm) *</label>
                  <input
                    type="number"
                    min="0.1"
                    max="250"
                    step="0.1"
                    value={formData.altura_x0020__x0028_cm_x0029_ || ""}
                    onChange={(event) =>
                      onFormChange("altura_x0020__x0028_cm_x0029_", parseFloat(event.target.value) || 0)
                    }
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: 170"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Peso (kg) *</label>
                  <input
                    type="number"
                    min="0.1"
                    max="300"
                    step="0.1"
                    value={formData.peso || ""}
                    onChange={(event) => onFormChange("peso", parseFloat(event.target.value) || 0)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: 70.5"
                    required
                  />
                </div>
              </div>

              {shouldShowImc && (
                <div className="mt-4 p-3 bg-white border border-green-300 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">IMC Calculado:</span>
                    <div className="text-right">
                      <span className="text-lg font-bold text-green-700">{imc.toFixed(1)}</span>
                      <div className="text-sm text-gray-600">{getIMCClassification(imc)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-4">Endereço</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <InputMask
                    mask="99999-999"
                    value={formData.patientAddressZipCode}
                    onChange={(event) => onFormChange("patientAddressZipCode", event.target.value)}
                    onBlur={(event) => onCepBlur(event.target.value)}
                  >
                    {(inputProps: any) => (
                      <input
                        {...inputProps}
                        type="text"
                        className="w-full rounded-md border-gray-300 shadow-sm"
                        placeholder="Digite o CEP"
                      />
                    )}
                  </InputMask>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rua</label>
                  <input
                    type="text"
                    value={formData.patientAddressStreet}
                    onChange={(event) => onFormChange("patientAddressStreet", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input
                    type="number"
                    value={formData.patientAddressNumber}
                    onChange={(event) => onFormChange("patientAddressNumber", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input
                    type="text"
                    value={formData.patientAddressComplement}
                    onChange={(event) => onFormChange("patientAddressComplement", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input
                    type="text"
                    value={formData.patientAddressNeighborhood}
                    onChange={(event) => onFormChange("patientAddressNeighborhood", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input
                    type="text"
                    value={formData.patientAddressCity}
                    onChange={(event) => onFormChange("patientAddressCity", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input
                    type="text"
                    value={formData.patientAddressState}
                    onChange={(event) => onFormChange("patientAddressState", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-4">Contato de Emergência</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={formData.patientEmergencyContactName}
                    onChange={(event) => onFormChange("patientEmergencyContactName", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <InputMask
                    mask="(99) 99999-9999"
                    value={formData.patientEmergencyContactPhone}
                    onChange={(event) => onFormChange("patientEmergencyContactPhone", event.target.value)}
                  >
                    {(inputProps: any) => (
                      <input {...inputProps} type="text" className="w-full rounded-md border-gray-300 shadow-sm" />
                    )}
                  </InputMask>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parentesco</label>
                  <input
                    type="text"
                    value={formData.patientEmergencyContactRelationship}
                    onChange={(event) => onFormChange("patientEmergencyContactRelationship", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-cyan-900 mb-4">Informações Médicas Adicionais</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Sanguíneo</label>
                  <select
                    value={formData.patientMedicalInfoBloodType}
                    onChange={(event) => onFormChange("patientMedicalInfoBloodType", event.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Selecione</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alergias (separadas por vírgula)
                  </label>
                  <textarea
                    value={formData.patientMedicalInfoAllergies}
                    onChange={(event) => onFormChange("patientMedicalInfoAllergies", event.target.value)}
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medicamentos em uso (separados por vírgula)
                  </label>
                  <textarea
                    value={formData.patientMedicalInfoMedications}
                    onChange={(event) => onFormChange("patientMedicalInfoMedications", event.target.value)}
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-900 mb-4">Informações Médicas</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Diagnósticos</label>
                <textarea
                  value={formData.diagn_x00f3_sticos}
                  onChange={(event) => onFormChange("diagn_x00f3_sticos", event.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Descreva os diagnósticos e condições médicas do paciente..."
                />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Consentimento (LGPD)</h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(event) => onConsentChange(event.target.checked)}
                  className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                  required
                />
                <span className="text-sm text-neutral-700">
                  Confirmo que o paciente consentiu com a coleta e o tratamento de seus dados pessoais e de
                  saúde para fins terapêuticos, conforme a LGPD.
                </span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !consentGiven}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Cadastrar Paciente
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PatientRegistrationModal;
